import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import Image from "next/image";
import { StarIcon } from "@/components/icons/StarIcon";
import { Pagination } from "swiper/modules";
import "@/styles/swiper-overrides.css";

type Testimonial = { name: string; role: string; text: string };

const testimonials: Testimonial[] = [
  {
    name: "Amanda Ruiz",
    role: "Head of Fulfillment",
    text: "With Nest Shipper, we’ve cut our logistics spend by nearly 40%. The automated label generation alone saves us hours every week.",
  },
  {
    name: "Martin Kelley",
    role: "Operations Director",
    text: "We connected our WooCommerce and Amazon stores in minutes. Nest Shipper streamlined our entire post-sale operation.",
  },
  {
    name: "Sophie Lin",
    role: "Logistics Lead",
    text: "Shipping labels used to be a bottleneck. Nest Shipper’s system is lightning fast, even when we process 500+ daily orders.",
  },
  {
    name: "James Alvarez",
    role: "CTO",
    text: "We’ve tested multiple shipping platforms. Nest Shipper offers by far the most intuitive UI and the best carrier discounts we’ve seen.",
  },
  {
    name: "Claire Nguyen",
    role: "Co-Founder",
    text: "International orders used to be a headache. Nest Shipper’s global carrier support and live rate comparisons changed that instantly.",
  },
  {
    name: "David Ross",
    role: "VP of Logistics",
    text: "Nest Shipper helped us scale from 2 to 8 warehouses while keeping shipping costs under control. That’s operational gold.",
  },
  {
    name: "Lisa Grant",
    role: "Fulfillment Manager",
    text: "We ship over 1,000 items a day. Nest Shipper not only keeps up but simplifies the entire process with powerful automations.",
  },
  {
    name: "Neal Thomas",
    role: "CFO",
    text: "Since switching to Nest Shipper, we’re saving over $6,000 a month on carrier fees. That’s money we now reinvest into growth.",
  },
  {
    name: "Angela Romero",
    role: "Ecommerce Director",
    text: "Being able to manage Shopify, Etsy, and wholesale orders from one dashboard has made our shipping team twice as efficient.",
  },
  {
    name: "Tom Becker",
    role: "CEO",
    text: "The onboarding was smooth, the integrations were instant, and support has been top-notch.",
  },
];

const TestimonialSection = () => {
  const theme = useTheme();
  const isLargeMobile = useMediaQuery("(max-width: 1199px)");
  const isMobile = useMediaQuery("(max-width: 599px)");

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#f5f5fa",
        overflow: "hidden",
        mb: { xs: 6, md: 8 },
      }}
    >
      {/* Ellipses */}
      {!isLargeMobile && (
        <>
          <Image
            src="/images/EllipseYellow.png"
            alt="Yellow Ellipse"
            width={400}
            height={400}
            style={{
              position: "absolute",
              top: "0px",
              left: "200px",
              zIndex: 1,
            }}
          />
          <Image
            src="/images/EllipseGreen.png"
            alt="Green Ellipse"
            width={400}
            height={400}
            style={{
              position: "absolute",
              bottom: "0px",
              right: "0px",
              zIndex: 0,
            }}
          />
        </>
      )}

      {/* Title */}
      <Box
        sx={{
          maxWidth: "1420px",
          mx: "auto",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-poppins)",
            fontSize: { xs: "24px", md: "32px" },
            fontWeight: 800,
            color: theme.palette.primary.main,
            mb: { xs: 2, md: 3 },
          }}
        >
          What our customers say about Nest Shipper
        </Typography>
        <Typography
          sx={{
            fontFamily: "RethinkSans, sans-serif",
            fontSize: { xs: "16px", md: "20px" },
            color: "#394e2d",
            mb: { xs: 4, md: 6 },
            px: { xs: 1, md: 0 },
          }}
        >
          From fast-growing startups to enterprise brands, businesses rely on
          Nest Shipper to streamline shipping and boost profitability.
        </Typography>
      </Box>

      {/* Testimonials */}
      {isMobile ? (
        <Swiper
          spaceBetween={isMobile ? 100 : 20}
          slidesPerView={1}
          modules={[Pagination]}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          style={{
            maxWidth: "90%",
            margin: "auto",
            zIndex: 2,
            paddingBottom: "30px",
          }}
        >
          {testimonials.map((t, i) => (
            <SwiperSlide key={i}>
              <TestimonialCard {...t} />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: "30px",
            px: 2,
            mt: 4,
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "50px",
              zIndex: 2,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "30px",
              animation: "scroll 60s linear infinite",
            }}
          >
            {[...testimonials, ...testimonials].map((t, i) => (
              <TestimonialCard key={i} {...t} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const TestimonialCard = ({ name, role, text }: Testimonial) => (
  <Box
    sx={{
      width: { xs: "100%", sm: "100%", md: "404px" },
      zIndex: 1,
      maxWidth: { xs: "304px", sm: "404px" },
      height: { xs: "200px", sm: "251px" },
      flexShrink: 0,
      borderRadius: "20px",
      backgroundColor: "#fff",
      padding: "31.5px 30.3px",
      boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.05)",
      mx: "auto",
    }}
  >
    <Typography
      sx={{
        fontFamily: "Poppins",
        fontSize: "17px",
        fontWeight: 800,
        color: "#394e2d",
        lineHeight: 2.65,
        mb: -2,
      }}
    >
      {name}
    </Typography>
    <Typography
      sx={{
        fontFamily: "Poppins",
        fontSize: "15px",
        color: "#7a7c7f",
        lineHeight: 2.93,
        mb: 2,
      }}
    >
      {role}
    </Typography>
    <Typography
      sx={{
        fontFamily: "RethinkSans",
        fontSize: "16px",
        color: "#212529",
        lineHeight: 1.25,
        mb: "25.8px",
      }}
    >
      {text}
    </Typography>
    <Box sx={{ display: "flex", gap: "4px" }}>
      {[...Array(5)].map((_, idx) => (
        <StarIcon key={idx} sx={{ fontSize: 20 }} />
      ))}
    </Box>
  </Box>
);

export default TestimonialSection;
